import { useContext } from 'react';

import { useGetSegmentInfoQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import {
  selectBinSize,
  selectCapacityLanes,
  selectCapacityThreshold,
  selectCurrentProjectInfo,
  selectSegmentId,
  selectTypicalMonth,
} from 'state/workflowSlice';

import dayjs from 'dayjs';
import {
  ExportType,
  triggerCsvExport,
  useExportFilename,
} from 'features/common/exportUtils';
import { PlanningTableDataExpander } from 'features/table/PlanningTableDataExpander';

import { DayTypes } from '../../appConstants';
import { LayerContext } from '../../state/LayerContext';
import { usePlanningVolumesDataProvider } from './planningVolumesCommon';

export function useVolumeExportData(skip?: boolean) {
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const segmentId = useAppSelector(selectSegmentId);
  const binSize = useAppSelector(selectBinSize);
  const capacityThreshold = useAppSelector(selectCapacityThreshold);
  const capacityLanes = useAppSelector(selectCapacityLanes);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const filename = useExportFilename(ExportType.Data);
  // cached so no need to memoize
  const { layer } = useContext(LayerContext);
  const { currentData: segmentData } = useGetSegmentInfoQuery(
    { layerId: layer, routeIds: [segmentId] },
    { skip: skip || !layer || !segmentId },
  );

  const segment = segmentData && segmentData?.segments[segmentId];

  // typicalData from REST api
  const planningVolumeData = usePlanningVolumesDataProvider(skip);

  const handleExport = () => {
    if (skip) return;
    const dataExpander = new PlanningTableDataExpander(binSize);
    const data = dataExpander.expandData(planningVolumeData);
    const csv = [
      `Name, ${segment.properties.name}`,
      `ID, ${segmentId}`,
      segment.properties.cardinal_dir
        ? `Direction, ${segment.properties.cardinal_dir}`
        : null,
      `Data period, ${typicalMonth}`,
      `Access date, ${dayjs().format('YYYY-MM-DD')}`,
      `Lane count, ${capacityLanes}`,
      `Lane capacity, ${capacityThreshold}`,
      `Total capacity, ${capacityThreshold * capacityLanes}`,
      '',
      '',
      userProject?.uses_middle_east_day_types
        ? 'Time Start, Time End,Sun-Thurs (hourly vehicle volume),Friday (hourly vehicle volume),Saturday (hourly vehicle volume)'
        : 'Time Start, Time End,Mon-Fri (hourly vehicle volume),Saturday (hourly vehicle volume),Sunday (hourly vehicle volume)',
    ];
    for (let i = 0; i < data.length; i += 1) {
      const row = data[i];
      const time = row.Time.split(' - ');
      const start = time[0];
      const end = time.length > 1 ? time[1] : start;
      if (!userProject?.uses_middle_east_day_types) {
        csv.push(
          `${start}, ${end},${row[DayTypes.Weekday] || ''},${
            row[DayTypes.Saturday] || ''
          },${row[DayTypes.Sunday] || ''}`,
        );
      } else {
        csv.push(
          `${start}, ${end},${row[DayTypes.MeWeekday] || ''},${
            row[DayTypes.Friday] || ''
          },${row[DayTypes.Saturday] || ''}`,
        );
      }
    }
    triggerCsvExport(filename, csv);
  };

  const isActive = !!planningVolumeData;

  return { handleExport, isActive };
}
